import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Database, RefreshCw, Check, X, Edit2, Save, TrendingUp, Package, ImageIcon, AlertCircle, Search, Image, ArrowRight, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ApiManagementPage = () => {
  const [activeTab, setActiveTab] = useState('api'); // 'api' or 'images'
  const [stats, setStats] = useState(null);
  const [apiConfigs, setApiConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [editingMargin, setEditingMargin] = useState(null);
  const [marginValue, setMarginValue] = useState('');
  const [error, setError] = useState(null);
  
  // Image Management State
  const [imageStats, setImageStats] = useState(null);
  const [searchOem, setSearchOem] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [autoAssigning, setAutoAssigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'images') {
      fetchImageStats();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setError(null);
      const [statsRes, configRes] = await Promise.all([
        axios.get(`${API}/api/admin/api-stats`),
        axios.get(`${API}/api/admin/api-configs`)
      ]);
      setStats(statsRes.data);
      setApiConfigs(configRes.data);
    } catch (err) {
      setError('Veriler yuklenemedi: ' + (err.message || 'Bilinmeyen hata'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchImageStats = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/image-management/stats`);
      setImageStats(res.data);
    } catch (err) {
      console.error('Image stats error:', err);
    }
  };

  const searchByOem = async () => {
    if (!searchOem.trim()) return;
    try {
      const res = await axios.get(`${API}/api/admin/image-management/search-oem/${encodeURIComponent(searchOem)}`);
      setSearchResults(res.data);
      setSelectedSource(null);
      setSelectedTargets([]);
    } catch (err) {
      alert('Arama hatasi: ' + err.message);
    }
  };

  const assignImages = async () => {
    if (!selectedSource || selectedTargets.length === 0) {
      alert('Lutfen kaynak ve hedef urunleri secin');
      return;
    }
    
    setAssigning(true);
    try {
      const res = await axios.post(`${API}/api/admin/image-management/assign`, {
        source_id: selectedSource,
        target_ids: selectedTargets
      });
      alert(`${res.data.updated_count} urune resim atandi!`);
      searchByOem(); // Refresh results
      fetchImageStats();
    } catch (err) {
      alert('Atama hatasi: ' + err.message);
    } finally {
      setAssigning(false);
    }
  };

  const autoAssignImages = async () => {
    if (!window.confirm('Otomatik resim atama islemini baslatmak istediginize emin misiniz?')) return;
    
    setAutoAssigning(true);
    try {
      const res = await axios.post(`${API}/api/admin/image-management/auto-assign`, { limit: 500 });
      alert(`Islem tamamlandi!\nGuncellenen: ${res.data.updated_count}\nAtlanamayan: ${res.data.skipped_count}`);
      fetchImageStats();
    } catch (err) {
      alert('Otomatik atama hatasi: ' + err.message);
    } finally {
      setAutoAssigning(false);
    }
  };

  const testConnection = async (apiId) => {
    setSyncing(prev => ({ ...prev, [apiId]: 'testing' }));
    try {
      const res = await axios.post(`${API}/api/admin/api-test/${apiId}`);
      alert(res.data.success ? 'Baglanti basarili!' : 'Baglanti hatasi: ' + res.data.error);
    } catch (err) {
      alert('Test basarisiz');
    } finally {
      setSyncing(prev => ({ ...prev, [apiId]: null }));
    }
  };

  const syncNow = async (apiId) => {
    setSyncing(prev => ({ ...prev, [apiId]: 'syncing' }));
    try {
      const res = await axios.post(`${API}/api/admin/api-sync/${apiId}`);
      alert(res.data.success ? `${res.data.synced} urun senkronize edildi!` : res.data.error);
      fetchData();
    } catch (err) {
      alert('Senkronizasyon basarisiz');
    } finally {
      setSyncing(prev => ({ ...prev, [apiId]: null }));
    }
  };

  const saveMargin = async (apiId) => {
    try {
      await axios.post(`${API}/api/admin/api-margin/${apiId}`, { margin: parseFloat(marginValue) });
      alert('Kar marji guncellendi');
      setEditingMargin(null);
      fetchData();
    } catch (err) {
      alert('Guncelleme basarisiz');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-orange-500 animate-spin" />
        <span className="ml-3 text-white">Yukleniyor...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-red-500 text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4" />
          <p>{error}</p>
          <button onClick={fetchData} className="mt-4 px-4 py-2 bg-orange-500 text-white rounded">
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Settings className="h-8 w-8 text-orange-500" />
              Sistem Yonetimi
            </h1>
            <p className="text-slate-400 mt-1">API kaynaklari ve resim yonetimi</p>
          </div>
          <button onClick={fetchData} className="px-4 py-2 border border-slate-600 rounded hover:bg-slate-800 flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Yenile
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('api')}
            className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === 'api' ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <Database className="h-5 w-5" />
            API Yonetimi
          </button>
          <button
            onClick={() => setActiveTab('images')}
            className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === 'images' ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <ImageIcon className="h-5 w-5" />
            Resim Yonetimi
          </button>
        </div>

        {activeTab === 'api' ? (
          <>
            {/* Genel Istatistikler */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Toplam Urun</p>
                    <p className="text-2xl font-bold text-white">{stats?.total_products?.toLocaleString()}</p>
                  </div>
                  <Package className="h-10 w-10 text-blue-500" />
                </div>
              </div>
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Stokta Var</p>
                    <p className="text-2xl font-bold text-green-400">{stats?.in_stock?.toLocaleString()}</p>
                  </div>
                  <Check className="h-10 w-10 text-green-500" />
                </div>
              </div>
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Resimli</p>
                    <p className="text-2xl font-bold text-purple-400">{stats?.with_image?.toLocaleString()}</p>
                  </div>
                  <ImageIcon className="h-10 w-10 text-purple-500" />
                </div>
              </div>
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Aktif API</p>
                    <p className="text-2xl font-bold text-orange-400">{(apiConfigs || []).filter(a => a.is_active).length}</p>
                  </div>
                  <Database className="h-10 w-10 text-orange-500" />
                </div>
              </div>
            </div>

            {/* API Kartlari */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {(Array.isArray(apiConfigs) ? apiConfigs : []).map(api => (
                <div key={api.id} className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                  <div className="border-b border-slate-700 p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold flex items-center gap-2">
                        <div className={"w-3 h-3 rounded-full " + (api.is_active ? 'bg-green-500' : 'bg-red-500')} />
                        {api.name}
                      </h3>
                      <span className="text-sm text-slate-400">{api.product_count?.toLocaleString()} urun</span>
                    </div>
                  </div>
                  <div className="p-4 space-y-4">
                    {/* Istatistikler */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-slate-700/50 rounded p-2">
                        <p className="text-xs text-slate-400">Stoklu</p>
                        <p className="font-bold text-green-400">{api.stats?.in_stock?.toLocaleString() || 0}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded p-2">
                        <p className="text-xs text-slate-400">Resimli</p>
                        <p className="font-bold text-purple-400">{api.stats?.with_image?.toLocaleString() || 0}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded p-2">
                        <p className="text-xs text-slate-400">Aktif</p>
                        <p className="font-bold text-blue-400">{api.stats?.active?.toLocaleString() || 0}</p>
                      </div>
                    </div>

                    {/* Kar Marji */}
                    <div className="flex items-center justify-between bg-slate-700/30 rounded p-3">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-orange-500" />
                        <span className="text-slate-300">Kar Marji:</span>
                      </div>
                      {editingMargin === api.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={marginValue}
                            onChange={(e) => setMarginValue(e.target.value)}
                            className="w-20 h-8 bg-slate-600 border border-slate-500 text-white rounded px-2"
                            placeholder="%"
                          />
                          <button onClick={() => saveMargin(api.id)} className="h-8 px-2 bg-green-600 hover:bg-green-700 rounded">
                            <Save className="h-4 w-4" />
                          </button>
                          <button onClick={() => setEditingMargin(null)} className="h-8 px-2 hover:bg-slate-600 rounded">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-bold text-orange-400">%{api.margin || 0}</span>
                          <button 
                            onClick={() => { setEditingMargin(api.id); setMarginValue(api.margin || 0); }}
                            className="h-8 px-2 hover:bg-slate-600 rounded"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Gelen Alanlar */}
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-sm text-slate-400 mb-2">Gelen Alanlar:</p>
                      <div className="flex flex-wrap gap-1">
                        {api.fields?.map(field => (
                          <span key={field} className="px-2 py-1 bg-slate-600 rounded text-xs text-slate-300">
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Son Senkronizasyon */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">
                        Son Sync: {api.last_sync ? new Date(api.last_sync).toLocaleString('tr-TR') : 'Hic'}
                      </span>
                      {api.sync_error && (
                        <span className="text-red-400 flex items-center gap-1">
                          <AlertCircle className="h-4 w-4" />
                          Hata
                        </span>
                      )}
                    </div>

                    {/* Aksiyonlar */}
                    <div className="flex gap-2">
                      <button 
                        onClick={() => testConnection(api.id)}
                        disabled={syncing[api.id]}
                        className="flex-1 px-4 py-2 border border-slate-600 rounded hover:bg-slate-700 flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {syncing[api.id] === 'testing' ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                        Test Et
                      </button>
                      <button 
                        onClick={() => syncNow(api.id)}
                        disabled={syncing[api.id]}
                        className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {syncing[api.id] === 'syncing' ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        Senkronize Et
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          /* Image Management Tab */
          <div className="space-y-6">
            {/* Image Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Toplam Urun</p>
                    <p className="text-2xl font-bold text-white">{imageStats?.total_products?.toLocaleString() || '-'}</p>
                  </div>
                  <Package className="h-10 w-10 text-blue-500" />
                </div>
              </div>
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Resimli</p>
                    <p className="text-2xl font-bold text-green-400">{imageStats?.with_image?.toLocaleString() || '-'}</p>
                  </div>
                  <Check className="h-10 w-10 text-green-500" />
                </div>
              </div>
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Resimsiz</p>
                    <p className="text-2xl font-bold text-red-400">{imageStats?.without_image?.toLocaleString() || '-'}</p>
                  </div>
                  <X className="h-10 w-10 text-red-500" />
                </div>
              </div>
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">OEM'li Resimsiz</p>
                    <p className="text-2xl font-bold text-yellow-400">{imageStats?.without_image_with_oem?.toLocaleString() || '-'}</p>
                  </div>
                  <ImageIcon className="h-10 w-10 text-yellow-500" />
                </div>
              </div>
            </div>

            {/* Auto Assign Button */}
            <div className="bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border border-orange-500/50 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <Zap className="h-6 w-6 text-yellow-400" />
                    Toplu Otomatik Resim Atama
                  </h3>
                  <p className="text-slate-400 mt-1">Ayni OEM koduna sahip urunlerden resim ata (500 urun/islem)</p>
                </div>
                <button
                  onClick={autoAssignImages}
                  disabled={autoAssigning}
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 rounded-lg flex items-center gap-2 disabled:opacity-50"
                >
                  {autoAssigning ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <Zap className="h-5 w-5" />
                  )}
                  Otomatik Ata
                </button>
              </div>
            </div>

            {/* OEM Search */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Search className="h-6 w-6 text-orange-500" />
                OEM Kodu ile Ara
              </h3>
              <div className="flex gap-4">
                <input
                  type="text"
                  value={searchOem}
                  onChange={(e) => setSearchOem(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchByOem()}
                  placeholder="OEM kodu girin (ornek: 28113-2H000)"
                  className="flex-1 px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400"
                />
                <button
                  onClick={searchByOem}
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 rounded-lg flex items-center gap-2"
                >
                  <Search className="h-5 w-5" />
                  Ara
                </button>
              </div>
            </div>

            {/* Search Results */}
            {searchResults && (
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold">
                    Sonuclar: {searchResults.oem_code}
                  </h3>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-400">Resimli: {searchResults.with_image}</span>
                    <span className="text-red-400">Resimsiz: {searchResults.without_image}</span>
                  </div>
                </div>

                {searchResults.products.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">Bu OEM kodunda urun bulunamadi</p>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      {searchResults.products.map(product => (
                        <div 
                          key={product.id}
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            selectedSource === product.id 
                              ? 'border-green-500 bg-green-500/10' 
                              : selectedTargets.includes(product.id)
                                ? 'border-orange-500 bg-orange-500/10'
                                : 'border-slate-600 hover:border-slate-500'
                          }`}
                          onClick={() => {
                            if (product.has_real_image) {
                              setSelectedSource(selectedSource === product.id ? null : product.id);
                            } else {
                              setSelectedTargets(prev => 
                                prev.includes(product.id) 
                                  ? prev.filter(id => id !== product.id)
                                  : [...prev, product.id]
                              );
                            }
                          }}
                        >
                          <div className="aspect-square bg-slate-700 rounded-lg mb-3 overflow-hidden relative">
                            {product.image_url && !product.image_url.includes('placeholder') ? (
                              <img 
                                src={product.image_url.split('|')[0]} 
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ImageIcon className="h-12 w-12 text-slate-500" />
                              </div>
                            )}
                            {product.has_real_image && (
                              <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                                KAYNAK
                              </div>
                            )}
                            {!product.has_real_image && (
                              <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                                RESİMSİZ
                              </div>
                            )}
                          </div>
                          <p className="font-medium text-sm line-clamp-2">{product.name}</p>
                          <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                            <span>{product.brand}</span>
                            <span>{product.source}</span>
                          </div>
                          <p className="text-orange-400 font-bold mt-1">₺{product.display_price?.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>

                    {/* Assign Button */}
                    {selectedSource && selectedTargets.length > 0 && (
                      <div className="flex items-center justify-between bg-slate-700/50 rounded-lg p-4">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-sm text-slate-400">Kaynak</p>
                            <p className="text-green-400 font-bold">1 urun secili</p>
                          </div>
                          <ArrowRight className="h-6 w-6 text-orange-500" />
                          <div className="text-center">
                            <p className="text-sm text-slate-400">Hedef</p>
                            <p className="text-orange-400 font-bold">{selectedTargets.length} urun secili</p>
                          </div>
                        </div>
                        <button
                          onClick={assignImages}
                          disabled={assigning}
                          className="px-6 py-3 bg-green-500 hover:bg-green-600 rounded-lg flex items-center gap-2 disabled:opacity-50"
                        >
                          {assigning ? (
                            <RefreshCw className="h-5 w-5 animate-spin" />
                          ) : (
                            <Image className="h-5 w-5" />
                          )}
                          Resimleri Ata
                        </button>
                      </div>
                    )}

                    {searchResults.without_image === 0 && (
                      <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 text-center">
                        <Check className="h-8 w-8 text-green-400 mx-auto mb-2" />
                        <p className="text-green-400">Bu OEM kodundaki tum urunlerde resim var!</p>
                      </div>
                    )}

                    {searchResults.with_image === 0 && searchResults.without_image > 0 && (
                      <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4 text-center">
                        <AlertCircle className="h-8 w-8 text-yellow-400 mx-auto mb-2" />
                        <p className="text-yellow-400">Bu OEM kodunda resimli urun yok. Baska bir kaynaktan resim eklenebilir.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiManagementPage;
